// R1: role counts over time (plan 5F), as pure functions. A sample is how many people held a role at a moment, taken
// hourly from one guild snapshot. `peaks` is what a chart plots, and it keeps ITCBot's shape on purpose:
// the last 24 hours raw, 7 days of daily peaks, 4 Monday-start weeks of weekly peaks. Two things differ from ITCBot's:
// a bucket with no sample is `null`, which a chart leaves as a gap (ITCBot drew it as 0, a drop to nothing), and the average is
// the average of the points plotted (ITCBot averaged every sample, so a chart of peaks quoted a lower number than it showed).

export const SPANS = ['day', 'week', 'month'] as const;
export type Span = (typeof SPANS)[number];

export const SPAN_LABELS: Readonly<Record<Span, string>> = { day: 'Day', week: 'Week', month: 'Month' };

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

export interface Sample {
  at: Date;
  count: number;
}

/** One plotted point. `count` is null for a bucket with no sample: a gap. */
export interface Point {
  at: Date;
  count: number | null;
}

export interface Peaks {
  points: Point[];
  /** The mean of the plotted points, leaving out the gaps. Null when nothing is plotted: not 0. */
  average: number | null;
}

/** How many days a role's samples are kept, at most: the setting's top. */
export const MAX_RETENTION_DAYS = 3650;

const dayStart = (at: Date): number => Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate());
/** Monday 00:00 UTC of the week holding `at` (Python's `weekday()` is 0 for Monday). */
const weekStart = (at: Date): number => dayStart(at) - ((at.getUTCDay() + 6) % 7) * DAY_MS;

const mean = (counts: readonly number[]): number | null => (counts.length === 0 ? null : counts.reduce((sum, count) => sum + count, 0) / counts.length);

/** Each bucket's peak, oldest first. A bucket that no sample falls in is null. */
function bucketed(samples: readonly Sample[], starts: readonly number[], width: number, bucketOf: (at: Date) => number): Point[] {
  const peak = new Map<number, number>();
  for (const sample of samples) {
    const start = bucketOf(sample.at);
    if (start < (starts[0] ?? 0) || start >= (starts[starts.length - 1] ?? 0) + width) continue;
    peak.set(start, Math.max(peak.get(start) ?? 0, sample.count));
  }
  return starts.map((start) => ({ at: new Date(start), count: peak.get(start) ?? null }));
}

/**
 * R1. `day`: the samples of the last 24 hours, as they are, oldest first. `week`: seven UTC days ending today, each its peak. `month`: four
 * weeks starting on a Monday (UTC), the last the one holding `now`, each its peak. A sample after `now` is ignored. A bucket with no sample
 * is `null`, and the average counts only what is plotted.
 */
export function peaks(samples: readonly Sample[], span: Span, now: Date): Peaks {
  const seen = samples.filter((sample) => sample.at.getTime() <= now.getTime());
  let points: Point[];
  if (span === 'day') {
    const cutoff = now.getTime() - DAY_MS;
    points = seen
      .filter((sample) => sample.at.getTime() > cutoff)
      .sort((a, b) => a.at.getTime() - b.at.getTime())
      .map((sample) => ({ at: sample.at, count: sample.count }));
  } else if (span === 'week') {
    const today = dayStart(now);
    points = bucketed(seen, Array.from({ length: 7 }, (_, i) => today - (6 - i) * DAY_MS), DAY_MS, (at) => dayStart(at));
  } else {
    const thisWeek = weekStart(now);
    points = bucketed(seen, Array.from({ length: 4 }, (_, i) => thisWeek - (3 - i) * 7 * DAY_MS), 7 * DAY_MS, (at) => weekStart(at));
  }
  return { points, average: mean(points.flatMap((point) => (point.count === null ? [] : [point.count]))) };
}

/** The oldest sample a span needs, so a read can leave the rest. */
export function spanStart(span: Span, now: Date): Date {
  if (span === 'day') return new Date(now.getTime() - DAY_MS);
  if (span === 'week') return new Date(dayStart(now) - 6 * DAY_MS);
  return new Date(weekStart(now) - 3 * 7 * DAY_MS);
}

/** Samples are taken every hour, so a gap of much more than that is a missing reading. */
export const SAMPLE_GAP_MS = 90 * 60_000;

/** The oldest a sample may be before it is pruned. */
export const retentionCutoff = (now: Date, days: number): Date => new Date(now.getTime() - days * DAY_MS);

/**
 * The tracked roles to sample: the setting's, with blanks and repeats left out, in the order given. Nothing is
 * read from Discord for none.
 */
export function trackedRoleIds(setting: readonly string[]): string[] {
  return [...new Set(setting.map((id) => id.trim()).filter((id) => id !== ''))];
}

/**
 * How many of a snapshot's people hold each role. A snapshot with nobody in it is a failed read, not a guild with nobody in it,
 * so it says nothing: null, never a row of zeros.
 */
export function countRoles(members: readonly { roles: readonly string[] }[], roleIds: readonly string[]): Map<string, number> | null {
  if (members.length === 0) return null;
  const counts = new Map<string, number>(roleIds.map((id): [string, number] => [id, 0]));
  for (const member of members) {
    for (const role of member.roles) {
      const held = counts.get(role);
      if (held !== undefined) counts.set(role, held + 1);
    }
  }
  return counts;
}
